const INTERNAL_UNCLASSIFIED_KEYS = new Set([
  '',
  'sin clasificar',
  'sin clasificacion canonica',
  '__sin_clasif__',
  '__unclassified__',
]);

const normalizeCategoryKey = (value: unknown) =>
  String(value ?? '')
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase();

const currentLanguage = () => localStorage.getItem('lang_id') || '1';

// Presentation only: the stored rank, color and date remain unchanged.
const CATEGORY_LABELS: Record<string, Record<string, string>> = {
  '1': { fiesta: 'Fiesta', memoria: 'Memoria Obligatoria', 'memoria libre': 'Memoria libre', solemnidad: 'Solemnidad', conmemoracion: 'Conmemoración' },
  '4': { fiesta: 'Feast', memoria: 'Obligatory Memorial', 'memoria libre': 'Optional Memorial', solemnidad: 'Solemnity', conmemoracion: 'Commemoration' },
  '5': { fiesta: 'Festa', memoria: 'Memoria obbligatoria', 'memoria libre': 'Memoria facoltativa', solemnidad: 'Solennità', conmemoracion: 'Commemorazione' },
  '6': { fiesta: 'Festa', memoria: 'Memória obrigatória', 'memoria libre': 'Memória facultativa', solemnidad: 'Solenidade', conmemoracion: 'Comemoração' },
  '7': { fiesta: 'Fête', memoria: 'Mémoire obligatoire', 'memoria libre': 'Mémoire facultative', solemnidad: 'Solennité', conmemoracion: 'Commémoraison' },
  '9': { fiesta: 'Festa', memoria: 'Memòria obligatòria', 'memoria libre': 'Memòria lliure', solemnidad: 'Solemnitat', conmemoracion: 'Commemoració' },
  '10': { fiesta: 'Festum', memoria: 'Memoria obligatoria', 'memoria libre': 'Memoria ad libitum', solemnidad: 'Sollemnitas', conmemoracion: 'Commemoratio' },
};

const COLOR_KEYS = ['blanco', 'rojo', 'verde', 'morado', 'rosa', 'negro', 'dorado'];
const COLOR_LABELS: Record<string, string[]> = {
  '1': ['Blanco', 'Rojo', 'Verde', 'Morado', 'Rosa', 'Negro', 'Dorado'],
  '4': ['White', 'Red', 'Green', 'Violet', 'Rose', 'Black', 'Gold'],
  '5': ['Bianco', 'Rosso', 'Verde', 'Viola', 'Rosa', 'Nero', 'Oro'],
  '6': ['Branco', 'Vermelho', 'Verde', 'Roxo', 'Rosa', 'Preto', 'Dourado'],
  '7': ['Blanc', 'Rouge', 'Vert', 'Violet', 'Rose', 'Noir', 'Or'],
  '9': ['Blanc', 'Vermell', 'Verd', 'Morat', 'Rosa', 'Negre', 'Daurat'],
  '10': ['Albus', 'Ruber', 'Viridis', 'Violaceus', 'Rosaceus', 'Niger', 'Aureus'],
};

const DATE_LOCALES: Record<string, string> = {
  '1': 'es-CO', '4': 'en-US', '5': 'it-IT', '6': 'pt-PT', '7': 'fr-FR', '9': 'ca-ES',
};
const LATIN_MONTHS = ['Ianuarii', 'Februarii', 'Martii', 'Aprilis', 'Maii', 'Iunii', 'Iulii', 'Augusti', 'Septembris', 'Octobris', 'Novembris', 'Decembris'];

export function formatSantoralDate(year: number, month: number, day: number, langId = currentLanguage()): string {
  if (langId === '10') return `${day} ${LATIN_MONTHS[month - 1]} ${year}`;
  // Build in UTC so a date-only celebration never shifts with the device timezone.
  return new Intl.DateTimeFormat(DATE_LOCALES[langId] || 'es-CO', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

export function getSantoralColor(value: unknown, langId = currentLanguage()): string {
  const raw = String(value ?? '').trim();
  const index = COLOR_KEYS.indexOf(normalizeCategoryKey(raw));
  return index < 0 ? raw : (COLOR_LABELS[langId] || COLOR_LABELS['1'])[index];
}

export function getPublicSantoralCategory(value: unknown, langId = currentLanguage()): string | null {
  const raw = String(value ?? '').trim();
  const key = normalizeCategoryKey(raw);

  if (INTERNAL_UNCLASSIFIED_KEYS.has(key)) {
    return null;
  }

  const canonicalKey = key === 'memoria obligatoria' ? 'memoria' : key;
  return (CATEGORY_LABELS[langId] || CATEGORY_LABELS['1'])[canonicalKey] || raw;
}

export function formatSantoralIdentity(
  name: unknown,
  category: unknown,
  langId = currentLanguage()
): string {
  const cleanName = String(name ?? '').trim();
  const publicCategory = getPublicSantoralCategory(category, langId);

  return publicCategory
    ? `${cleanName}, ${publicCategory}.`
    : cleanName;
}
